import { useState } from 'react'
import {
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Copy,
  Package,
  Plus,
  Save,
  Trash2,
} from 'lucide-react'
import {
  MANIFEST_1302_DISCREPANCY_OPTIONS,
  MANIFEST_1302_INTAKE_SOURCE_OPTIONS,
  MANIFEST_1302_RISK_OPTIONS,
  MANIFEST_1302_SECTIONS,
  MANIFEST_1302_SHIPMENT_FIELDS,
} from '@/features/case-workspace/manifest1302'
import { DOCUMENT_DEFINITIONS } from '@/features/case-workspace/schemas'
import type {
  DocumentFieldDefinition,
  DocumentWorkspaceFocusTarget,
  StructuredDocumentRecord,
} from '@/features/case-workspace/types'
import { formatDateTime } from '@/lib/cases'
import {
  FieldControl,
  InlineHelp,
  OptionalSectionNote,
  SectionHeading,
  StatusPill,
  toneForRecommendationState,
} from '@/features/case-workspace/WorkspaceShared'
import { canMarkDocumentComplete, canMarkSectionComplete, isFieldVisible, summarizeSectionStatus } from '@/features/case-workspace/utils'
import { summarizeValidationIssues } from '@/features/case-workspace/mockData'

type Manifest1302EditorProps = {
  record: StructuredDocumentRecord
  focusTarget: DocumentWorkspaceFocusTarget | null
  busy: boolean
  saveMessage: string | null
  onFieldChange: (recordId: string, fieldKey: string, nextValue: string) => void
  onFieldNoteChange: (recordId: string, fieldKey: string, note: string) => void
  onConfirmField: (recordId: string, fieldKey: string) => void
  onSectionNoteChange: (recordId: string, sectionId: string, note: string) => void
  onMarkSectionComplete: (recordId: string, sectionId: string) => void
  onLineItemFieldChange: (recordId: string, lineItemId: string, fieldKey: string, nextValue: string) => void
  onLineItemNoteChange: (recordId: string, lineItemId: string, note: string) => void
  onConfirmLineItemField: (recordId: string, lineItemId: string, fieldKey: string) => void
  onAddLineItem: (recordId: string) => void
  onDuplicateLineItem: (recordId: string, lineItemId: string) => void
  onRemoveLineItem: (recordId: string, lineItemId: string) => void
  onMoveLineItem: (recordId: string, lineItemId: string, direction: 'up' | 'down') => void
  onApplyRecommendation: (recordId: string, recommendationId: string) => void
  onSave: (recordId: string) => Promise<void>
  onAutoSave: (recordId: string, options?: { auto?: boolean }) => Promise<void>
  onMarkComplete: (recordId: string) => void
}

const SHIPMENT_GROUPS = [
  {
    id: 'bill',
    title: 'Bill / Shipment Details',
    fields: ['bill_of_lading_number', 'master_bill_number', 'house_bill_number', 'bill_date', 'first_port_place_possession', 'shipment_reconciliation_status'],
  },
  {
    id: 'parties',
    title: 'Parties',
    fields: ['shipper_name', 'shipper_address', 'consignee_name', 'consignee_address', 'notify_party_name', 'notify_party_address'],
  },
  {
    id: 'cargo',
    title: 'Cargo Details',
    fields: ['goods_description', 'gross_weight', 'weight_unit', 'measurement_volume', 'country_of_origin', 'country_of_export', 'hazardous_indicator', 'refrigerated_indicator'],
  },
  {
    id: 'containers',
    title: 'Containers / Seals / Packages',
    fields: ['marks_and_numbers', 'container_numbers', 'seal_numbers', 'number_of_packages', 'kind_of_packages'],
  },
]

function labelForSectionStatus(status: ReturnType<typeof summarizeSectionStatus>) {
  switch (status) {
    case 'not_started':
      return 'Not Started'
    case 'in_progress':
      return 'In Progress'
    case 'needs_review':
      return 'Needs Review'
    case 'complete':
      return 'Complete'
    case 'validation_error':
      return 'Validation Error'
    case 'cross_document_conflict':
      return 'Cross-Document Conflict'
  }
}

function toneForSectionStatus(status: ReturnType<typeof summarizeSectionStatus>) {
  switch (status) {
    case 'complete':
      return 'success'
    case 'needs_review':
      return 'warning'
    case 'validation_error':
    case 'cross_document_conflict':
      return 'error'
    case 'in_progress':
      return 'info'
    default:
      return 'neutral'
  }
}

function labelForFieldReviewStatus(status?: StructuredDocumentRecord['fields'][string]['reviewStatus']) {
  switch (status) {
    case 'extracted':
      return 'Extracted'
    case 'manually_entered':
      return 'Manually entered'
    case 'edited_after_extraction':
      return 'Edited after extraction'
    case 'agent_confirmed':
      return 'Agent confirmed'
    case 'unresolved_issue':
      return 'Unresolved'
    default:
      return null
  }
}

function optionLabel(options: Array<{ value: string; label: string }>, value?: string) {
  return options.find((option) => option.value === value)?.label ?? 'Unknown / Needs Review'
}

function countPopulatedFields(record: StructuredDocumentRecord, fields: DocumentFieldDefinition[]) {
  const visibleFields = fields.filter((field) => isFieldVisible(field, record.fields))
  const populatedCount = visibleFields.filter((field) => record.fields[field.key]?.value?.trim()).length
  return { populatedCount, totalCount: visibleFields.length }
}

export function Manifest1302Editor({
  record,
  focusTarget,
  busy,
  saveMessage,
  onFieldChange,
  onFieldNoteChange,
  onConfirmField,
  onSectionNoteChange,
  onMarkSectionComplete,
  onLineItemFieldChange,
  onLineItemNoteChange,
  onConfirmLineItemField,
  onAddLineItem,
  onDuplicateLineItem,
  onRemoveLineItem,
  onMoveLineItem,
  onApplyRecommendation,
  onSave,
  onAutoSave,
  onMarkComplete,
}: Manifest1302EditorProps) {
  const definition = DOCUMENT_DEFINITIONS.manifest
  const lineItems = record.lineItems ?? []
  const sectionStates = record.sectionStates ?? {}
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({})
  const [expandedShipmentIds, setExpandedShipmentIds] = useState<string[]>(
    lineItems.slice(0, 1).map((shipment) => shipment.id),
  )
  const issueMap = record.validationIssues.reduce<Record<string, string[]>>((acc, issue) => {
    acc[issue.fieldKey] = [...(acc[issue.fieldKey] ?? []), issue.message]
    return acc
  }, {})
  const recommendationMap = record.awaRecommendations.reduce<Record<string, StructuredDocumentRecord['awaRecommendations']>>((acc, recommendation) => {
    acc[recommendation.fieldKey] = [...(acc[recommendation.fieldKey] ?? []), recommendation]
    return acc
  }, {})
  const validationSummary = summarizeValidationIssues(record.validationIssues)
  const intakeSourceLabel = optionLabel(
    MANIFEST_1302_INTAKE_SOURCE_OPTIONS,
    record.fields.manifest_intake_source?.value,
  )
  const discrepancyLabel = optionLabel(
    MANIFEST_1302_DISCREPANCY_OPTIONS,
    record.fields.cross_document_discrepancy_status?.value,
  )
  const riskLabel = optionLabel(MANIFEST_1302_RISK_OPTIONS, record.fields.risk_severity?.value)
  const unresolvedRecommendations = record.awaRecommendations.filter((recommendation) => recommendation.state !== 'resolved').length
  const canCompleteDocument = canMarkDocumentComplete(record)
  const isDocumentComplete = record.status === 'complete'

  function toggleSection(sectionId: string, canCollapse: boolean) {
    setOpenSections((current) => {
      const definitionSection = MANIFEST_1302_SECTIONS.find((section) => section.id === sectionId)
      const isComplete = sectionStates[sectionId]?.status === 'complete'
      const isOpen = current[sectionId] ?? (isComplete ? false : !definitionSection?.collapsedByDefault)
      if (isOpen && !canCollapse) return current
      return { ...current, [sectionId]: !isOpen }
    })
  }

  function toggleShipment(shipmentId: string) {
    setExpandedShipmentIds((current) =>
      current.includes(shipmentId)
        ? current.filter((id) => id !== shipmentId)
        : [...current, shipmentId],
    )
  }

  return (
    <div className="space-y-3">
      <section className="rounded-lg border border-neutral-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
          <div className="min-w-0">
            <SectionHeading
              eyebrow="CBP Form 1302 Manifest Review"
              title={record.title}
              helpText="Use the grouped manifest workspace to review vessel, routing, shipment, party, cargo, and reconciliation details tied to CBP Form 1302 concepts."
            />
            <div className="mt-3 flex flex-wrap gap-2">
              <StatusPill label={intakeSourceLabel} tone="info" />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => void onSave(record.id)}
              disabled={busy}
              className="inline-flex items-center gap-2 rounded border border-secondary px-4 py-2 text-sm font-semibold text-secondary hover:bg-secondary-lightest disabled:opacity-60"
            >
              <Save size={16} aria-hidden="true" />
              {busy ? 'Saving…' : 'Save progress'}
            </button>
            <button
              type="button"
              onClick={() => onMarkComplete(record.id)}
              disabled={!isDocumentComplete && !canCompleteDocument}
              className={`inline-flex items-center gap-2 rounded px-4 py-2 text-sm font-semibold ${
                isDocumentComplete
                  ? 'bg-green-600 text-white'
                  : 'bg-primary text-white hover:bg-primary-dark disabled:cursor-not-allowed disabled:bg-neutral-300 disabled:text-neutral-600'
              }`}
            >
              <CheckCircle2 size={16} aria-hidden="true" />
              {isDocumentComplete ? 'Complete' : 'Mark complete'}
            </button>
          </div>
        </div>

        <div className="mt-3 grid gap-2 xl:grid-cols-4">
          <SummaryValue label="Shipment rows" value={`${lineItems.length}`} />
          <SummaryValue label="Blocking issues" value={`${validationSummary.errors}`} />
          <SummaryValue label="Open AWA items" value={`${unresolvedRecommendations}`} />
          <SummaryValue label="Last updated" value={formatDateTime(record.lastUpdatedAt)} />
        </div>

        <div className="mt-4 grid gap-4 lg:grid-cols-3">
          <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-4">
            <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Manifest intake source</div>
            <div className="mt-2 text-sm font-semibold text-primary">{intakeSourceLabel}</div>
            <p className="mt-2 text-sm text-neutral-600">
              {record.fields.manifest_intake_source?.value === 'ace_direct_transmission'
                ? 'Electronically received from ACE. System timestamps and carrier metadata should be prioritized first.'
                : record.fields.manifest_intake_source?.value === 'port_arrival_document'
                  ? 'Provided upon arrival to port. Review the extracted fields carefully and confirm any critical blanks before acceptance.'
                  : record.fields.manifest_intake_source?.value === 'vecs_submission'
                    ? 'Submitted through VECS. Confirm the routed vessel-clearance metadata and any remaining paper-era fields.'
                    : 'Manifest source still needs confirmation.'}
            </p>
          </div>
          <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-4">
            <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Cross-document status</div>
            <div className="mt-2 text-sm font-semibold text-primary">{discrepancyLabel}</div>
            <p className="mt-2 text-sm text-neutral-600">
              Track whether the manifest matches the 7501 and related case documents, or whether a manual reconciliation decision is still needed.
            </p>
          </div>
          <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-4">
            <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Risk severity</div>
            <div className="mt-2 text-sm font-semibold text-primary">{riskLabel}</div>
            <p className="mt-2 text-sm text-neutral-600">
              Use this to show whether current manifest issues are informational, operationally significant, or blocking acceptance and transmit.
            </p>
          </div>
        </div>

        {saveMessage && <p className="mt-3 text-sm text-success">{saveMessage}</p>}
      </section>

      {definition.sections.map((section) => {
        const sectionStatus = summarizeSectionStatus(record, section.id)
        const canCompleteSection = canMarkSectionComplete(record, section.id)
        const canCollapseSection = sectionStatus === 'complete'
        const isOpen = openSections[section.id] ?? (sectionStatus === 'complete' ? false : !section.collapsedByDefault)

        return (
          <section key={section.id} className="rounded-lg border border-neutral-200 bg-white shadow-sm">
            <button
              type="button"
              onClick={() => toggleSection(section.id, canCollapseSection)}
              className="flex w-full items-start justify-between gap-4 px-5 py-4 text-left"
            >
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h4 className="text-base font-bold text-primary">{section.title}</h4>
                  <StatusPill label={labelForSectionStatus(sectionStatus)} tone={toneForSectionStatus(sectionStatus)} />
                  {section.kind !== 'line_items' && <SectionProgressBadge record={record} fields={section.fields} />}
                </div>
              </div>
              {isOpen ? <ChevronDown size={18} aria-hidden="true" /> : <ChevronRight size={18} aria-hidden="true" />}
            </button>

            {isOpen && (
              <div className="space-y-4 border-t border-neutral-200 px-5 py-5">
                {section.description && (
                  <div className="text-sm text-neutral-600">
                    <InlineHelp text={section.description} title={`${section.title} help`} align="left" />
                  </div>
                )}
                {section.kind === 'line_items' ? (
                  <ManifestShipmentSection
                    record={record}
                    focusTarget={focusTarget}
                    onLineItemFieldChange={onLineItemFieldChange}
                    onLineItemNoteChange={onLineItemNoteChange}
                    onConfirmLineItemField={onConfirmLineItemField}
                    onAddLineItem={onAddLineItem}
                    onDuplicateLineItem={onDuplicateLineItem}
                    onRemoveLineItem={onRemoveLineItem}
                    onMoveLineItem={onMoveLineItem}
                    expandedShipmentIds={expandedShipmentIds}
                    toggleShipment={toggleShipment}
                    issueMap={issueMap}
                    onAutoSave={onAutoSave}
                  />
                ) : (
                  <div className="grid gap-4 md:grid-cols-2">
                    {section.fields
                      .filter((field) => isFieldVisible(field, record.fields))
                      .map((field) => (
                        <ManifestFieldCard
                          key={field.key}
                          record={record}
                          field={field}
                          focusTarget={focusTarget}
                          issues={issueMap[field.key] ?? []}
                          recommendations={recommendationMap[field.key] ?? []}
                          onFieldChange={onFieldChange}
                          onFieldNoteChange={onFieldNoteChange}
                          onConfirmField={onConfirmField}
                          onApplyRecommendation={onApplyRecommendation}
                          onAutoSave={onAutoSave}
                        />
                      ))}
                  </div>
                )}

                <OptionalSectionNote
                  label="Section Review"
                  value={sectionStates[section.id]?.note ?? ''}
                  onChange={(nextValue) => onSectionNoteChange(record.id, section.id, nextValue)}
                  onBlur={() => void onAutoSave(record.id, { auto: true })}
                  placeholder="Add an optional note for this section."
                  actions={
                    <button
                      type="button"
                      onClick={() => {
                        onMarkSectionComplete(record.id, section.id)
                        void onAutoSave(record.id, { auto: true })
                        if (sectionStatus === 'complete') {
                          setOpenSections((current) => ({ ...current, [section.id]: true }))
                        } else {
                          setOpenSections((current) => ({ ...current, [section.id]: false }))
                        }
                      }}
                      disabled={sectionStatus !== 'complete' && !canCompleteSection}
                      className={`rounded px-2.5 py-1 text-xs font-medium ${
                        sectionStatus === 'complete'
                          ? 'bg-green-600 text-white'
                          : 'bg-primary text-white hover:bg-primary-dark disabled:cursor-not-allowed disabled:bg-neutral-300 disabled:text-neutral-600'
                      }`}
                    >
                      {sectionStatus === 'complete' ? 'Complete' : 'Mark as Complete'}
                    </button>
                  }
                />
              </div>
            )}
          </section>
        )
      })}
    </div>
  )
}

function SummaryValue({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-neutral-200 bg-neutral-50 px-4 py-3">
      <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500">{label}</div>
      <div className="mt-2 text-sm font-semibold text-primary">{value}</div>
    </div>
  )
}

function SectionProgressBadge({
  record,
  fields,
}: {
  record: StructuredDocumentRecord
  fields: DocumentFieldDefinition[]
}) {
  const { populatedCount, totalCount } = countPopulatedFields(record, fields)
  return <span className="text-xs font-medium text-neutral-500">{`${populatedCount}/${totalCount} fields`}</span>
}

function ManifestFieldCard({
  record,
  field,
  focusTarget,
  issues,
  recommendations,
  onFieldChange,
  onFieldNoteChange,
  onConfirmField,
  onApplyRecommendation,
  onAutoSave,
}: {
  record: StructuredDocumentRecord
  field: DocumentFieldDefinition
  focusTarget: DocumentWorkspaceFocusTarget | null
  issues: string[]
  recommendations: StructuredDocumentRecord['awaRecommendations']
  onFieldChange: (recordId: string, fieldKey: string, nextValue: string) => void
  onFieldNoteChange: (recordId: string, fieldKey: string, note: string) => void
  onConfirmField: (recordId: string, fieldKey: string) => void
  onApplyRecommendation: (recordId: string, recommendationId: string) => void
  onAutoSave: (recordId: string, options?: { auto?: boolean }) => Promise<void>
}) {
  const fieldState = record.fields[field.key]
  const isFocused = focusTarget?.documentId === record.id && focusTarget.fieldKey === field.key
  const reviewLabel = labelForFieldReviewStatus(fieldState?.reviewStatus)
  const isAgentConfirmed = fieldState?.reviewStatus === 'agent_confirmed'

  return (
    <div className={`rounded-lg border p-4 ${field.type === 'textarea' ? 'md:col-span-2' : ''} ${isFocused ? 'border-accent-sunset bg-accent-sunset/5' : 'border-neutral-200'}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <div className="text-sm font-semibold text-primary">{field.label}</div>
            {field.helpText && <InlineHelp text={field.helpText} title={`${field.label} help`} align="left" />}
          </div>
          {field.officialName && <div className="mt-1 text-xs text-neutral-500">{field.officialName}</div>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {reviewLabel && fieldState?.reviewStatus !== 'agent_confirmed' && (
            <StatusPill label={reviewLabel} tone="info" />
          )}
        </div>
      </div>
      <FieldControl
        fieldType={field.type}
        value={fieldState?.value ?? ''}
        options={field.options}
        placeholder={field.placeholder}
        onChange={(value) => onFieldChange(record.id, field.key, value)}
        onBlur={() => void onAutoSave(record.id, { auto: true })}
      />

      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-neutral-500">
        <span>Origin: {(fieldState?.origin ?? 'manual').replace(/_/g, ' ')}</span>
        {fieldState?.sourceValue && <span>Extracted: {fieldState.sourceValue}</span>}
        {fieldState?.sourceConfidence != null && <span>Confidence: {(fieldState.sourceConfidence * 100).toFixed(0)}%</span>}
        {field.enumSource && <span>Allowed values: {field.enumSource}</span>}
      </div>

      {fieldState?.conflictNote && (
        <div className="mt-2 rounded border border-warning/30 bg-warning/10 p-2 text-xs text-neutral-700">
          {fieldState.conflictNote}
        </div>
      )}

      {recommendations.length > 0 && (
        <div className="mt-3 space-y-2">
          {recommendations.map((recommendation) => (
            <div key={recommendation.id} className="rounded-lg border border-warning/30 bg-warning/10 p-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-primary">{recommendation.message}</div>
                  <div className="mt-1 text-xs text-neutral-600">
                    Current: {recommendation.currentValue || '—'}
                    {recommendation.recommendedValue ? ` · Recommended: ${recommendation.recommendedValue}` : ''}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <StatusPill label={recommendation.state.replace(/_/g, ' ')} tone={toneForRecommendationState(recommendation.state)} />
                  {recommendation.recommendedValue && (
                    <button
                      type="button"
                      onClick={() => onApplyRecommendation(record.id, recommendation.id)}
                      className="rounded border border-secondary px-2.5 py-1 text-xs font-semibold text-secondary hover:bg-secondary-lightest"
                    >
                      Apply recommendation
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {issues.length > 0 && (
        <ul className="mt-2 space-y-1 text-xs text-error">
          {issues.map((message) => (
            <li key={message}>{message}</li>
          ))}
        </ul>
      )}

      <div className="mt-3 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => onConfirmField(record.id, field.key)}
          disabled={isAgentConfirmed}
          className={`rounded px-3 py-1.5 text-xs font-semibold ${
            isAgentConfirmed
              ? 'bg-green-100 text-green-800'
              : 'border border-secondary text-secondary hover:bg-secondary-lightest'
          }`}
        >
          {isAgentConfirmed ? 'Agent Confirmed' : 'Mark confirmed'}
        </button>
        {fieldState?.agentConfirmedAt && (
          <span className="text-[11px] text-neutral-500">Confirmed {formatDateTime(fieldState.agentConfirmedAt)}</span>
        )}
      </div>

      <textarea
        rows={2}
        value={fieldState?.reviewNote ?? ''}
        onChange={(event) => onFieldNoteChange(record.id, field.key, event.target.value)}
        onBlur={() => void onAutoSave(record.id, { auto: true })}
        placeholder="Optional field review note"
        className="mt-3 w-full rounded border border-neutral-300 px-3 py-2 text-sm text-neutral-900"
      />
    </div>
  )
}

function ManifestShipmentSection({
  record,
  focusTarget,
  onLineItemFieldChange,
  onLineItemNoteChange,
  onConfirmLineItemField,
  onAddLineItem,
  onDuplicateLineItem,
  onRemoveLineItem,
  onMoveLineItem,
  expandedShipmentIds,
  toggleShipment,
  issueMap,
  onAutoSave,
}: {
  record: StructuredDocumentRecord
  focusTarget: DocumentWorkspaceFocusTarget | null
  onLineItemFieldChange: (recordId: string, lineItemId: string, fieldKey: string, nextValue: string) => void
  onLineItemNoteChange: (recordId: string, lineItemId: string, note: string) => void
  onConfirmLineItemField: (recordId: string, lineItemId: string, fieldKey: string) => void
  onAddLineItem: (recordId: string) => void
  onDuplicateLineItem: (recordId: string, lineItemId: string) => void
  onRemoveLineItem: (recordId: string, lineItemId: string) => void
  onMoveLineItem: (recordId: string, lineItemId: string, direction: 'up' | 'down') => void
  expandedShipmentIds: string[]
  toggleShipment: (lineItemId: string) => void
  issueMap: Record<string, string[]>
  onAutoSave: (recordId: string, options?: { auto?: boolean }) => Promise<void>
}) {
  const lineItems = record.lineItems ?? []
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm text-neutral-600">
          <span>Shipment and bill rows</span>
          <InlineHelp text="Expand a row to compare party, cargo, package, and container details in structured fields instead of free text." title="Shipment row help" align="left" />
        </div>
        <button
          type="button"
          onClick={() => onAddLineItem(record.id)}
          className="inline-flex items-center gap-2 rounded border border-secondary px-3 py-1.5 text-sm font-semibold text-secondary hover:bg-secondary-lightest"
        >
          <Plus size={14} aria-hidden="true" />
          Add shipment row
        </button>
      </div>

      <div className="overflow-x-auto rounded-lg border border-neutral-200">
        <div className="sticky top-0 z-10 grid min-w-[64rem] grid-cols-[5rem_12rem_minmax(0,2fr)_9rem_10rem_8rem] gap-3 border-b border-neutral-200 bg-neutral-50 px-4 py-3 text-xs font-semibold uppercase tracking-wider text-neutral-500">
          <div>Row</div>
          <div>Bill of Lading</div>
          <div>Description of Goods</div>
          <div>Packages</div>
          <div>Gross Weight</div>
          <div>Status</div>
        </div>

        <div className="divide-y divide-neutral-200">
          {lineItems.map((shipment, index) => {
            const isOpen = expandedShipmentIds.includes(shipment.id)
            const description = shipment.fields.goods_description?.value || 'No goods description yet'
            const billNumber = shipment.fields.bill_of_lading_number?.value || '—'
            const packageCount = shipment.fields.number_of_packages?.value || '—'
            const grossWeight = shipment.fields.gross_weight?.value || '—'
            const rowIssues = Object.entries(issueMap)
              .filter(([fieldKey]) => fieldKey.endsWith(`:${shipment.id}`))
              .flatMap(([, messages]) => messages)

            return (
              <div key={shipment.id} className="bg-white">
                <div className="grid min-w-[64rem] grid-cols-[5rem_12rem_minmax(0,2fr)_9rem_10rem_8rem] gap-3 px-4 py-3">
                  <button
                    type="button"
                    onClick={() => toggleShipment(shipment.id)}
                    className="inline-flex items-center gap-2 text-left text-sm font-semibold text-primary"
                  >
                    {isOpen ? <ChevronDown size={14} aria-hidden="true" /> : <ChevronRight size={14} aria-hidden="true" />}
                    {shipment.lineNumber}
                  </button>
                  <div className="text-sm text-neutral-700">{billNumber}</div>
                  <div className="text-sm text-neutral-700">{description}</div>
                  <div className="text-sm text-neutral-700">{packageCount}</div>
                  <div className="text-sm text-neutral-700">{grossWeight}</div>
                  <div>
                    <StatusPill label={labelForSectionStatus(shipment.status)} tone={toneForSectionStatus(shipment.status)} />
                  </div>
                </div>

                {isOpen && (
                  <div className="border-t border-neutral-200 bg-neutral-50 px-4 py-4">
                    <div className="mb-4 flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => onMoveLineItem(record.id, shipment.id, 'up')}
                        disabled={index === 0}
                        className="inline-flex items-center gap-1 rounded border border-neutral-300 px-2.5 py-1 text-xs font-semibold text-primary hover:border-secondary hover:bg-white disabled:opacity-50"
                      >
                        <ChevronUp size={12} aria-hidden="true" />
                        Move up
                      </button>
                      <button
                        type="button"
                        onClick={() => onMoveLineItem(record.id, shipment.id, 'down')}
                        disabled={index === lineItems.length - 1}
                        className="inline-flex items-center gap-1 rounded border border-neutral-300 px-2.5 py-1 text-xs font-semibold text-primary hover:border-secondary hover:bg-white disabled:opacity-50"
                      >
                        <ChevronDown size={12} aria-hidden="true" />
                        Move down
                      </button>
                      <button
                        type="button"
                        onClick={() => onDuplicateLineItem(record.id, shipment.id)}
                        className="inline-flex items-center gap-1 rounded border border-neutral-300 px-2.5 py-1 text-xs font-semibold text-primary hover:border-secondary hover:bg-white"
                      >
                        <Copy size={12} aria-hidden="true" />
                        Duplicate
                      </button>
                      <button
                        type="button"
                        onClick={() => onRemoveLineItem(record.id, shipment.id)}
                        disabled={lineItems.length === 1}
                        className="inline-flex items-center gap-1 rounded border border-error px-2.5 py-1 text-xs font-semibold text-error hover:bg-white disabled:opacity-50"
                      >
                        <Trash2 size={12} aria-hidden="true" />
                        Delete
                      </button>
                    </div>

                    <div className="space-y-4">
                      {SHIPMENT_GROUPS.map((group) => (
                        <div key={group.id} className="rounded-lg border border-neutral-200 bg-white p-4">
                          <div className="mb-3 flex items-center gap-2">
                            <Package size={14} aria-hidden="true" className="text-secondary" />
                            <h5 className="text-sm font-semibold text-primary">{group.title}</h5>
                          </div>
                          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                            {MANIFEST_1302_SHIPMENT_FIELDS.filter((field) => group.fields.includes(field.key)).map((field) => {
                              const fieldState = shipment.fields[field.key]
                              const reviewLabel = labelForFieldReviewStatus(fieldState?.reviewStatus)
                              const issues = issueMap[`${field.key}:${shipment.id}`] ?? []
                              const isFocused = focusTarget?.documentId === record.id && focusTarget.fieldKey === field.key
                              const isAgentConfirmed = fieldState?.reviewStatus === 'agent_confirmed'

                              return (
                                <div
                                  key={field.key}
                                  className={`rounded-lg border p-4 ${field.type === 'textarea' ? 'md:col-span-2 xl:col-span-3' : ''} ${isFocused ? 'border-accent-sunset bg-accent-sunset/5' : 'border-neutral-200 bg-white'}`}
                                >
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                      <div className="flex items-center gap-2">
                                        <div className="text-sm font-semibold text-primary">{field.label}</div>
                                        {field.helpText && <InlineHelp text={field.helpText} title={`${field.label} help`} align="left" />}
                                      </div>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                      {reviewLabel && fieldState?.reviewStatus !== 'agent_confirmed' && (
                                        <StatusPill label={reviewLabel} tone="info" />
                                      )}
                                    </div>
                                  </div>
                                  <FieldControl
                                    fieldType={field.type}
                                    value={fieldState?.value ?? ''}
                                    options={field.options}
                                    placeholder={field.placeholder}
                                    onChange={(value) => onLineItemFieldChange(record.id, shipment.id, field.key, value)}
                                    onBlur={() => void onAutoSave(record.id, { auto: true })}
                                  />
                                  <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-neutral-500">
                                    <span>Origin: {(fieldState?.origin ?? 'manual').replace(/_/g, ' ')}</span>
                                    {fieldState?.sourceValue && <span>Extracted: {fieldState.sourceValue}</span>}
                                    {fieldState?.sourceConfidence != null && <span>Confidence: {(fieldState.sourceConfidence * 100).toFixed(0)}%</span>}
                                  </div>
                                  {issues.length > 0 && (
                                    <ul className="mt-2 space-y-1 text-xs text-error">
                                      {issues.map((message) => (
                                        <li key={message}>{message}</li>
                                      ))}
                                    </ul>
                                  )}
                                  <div className="mt-3 flex items-center justify-between gap-3">
                                    <button
                                      type="button"
                                      onClick={() => onConfirmLineItemField(record.id, shipment.id, field.key)}
                                      disabled={isAgentConfirmed}
                                      className={`rounded px-3 py-1.5 text-xs font-semibold ${
                                        isAgentConfirmed
                                          ? 'bg-green-100 text-green-800'
                                          : 'border border-secondary text-secondary hover:bg-secondary-lightest'
                                      }`}
                                    >
                                      {isAgentConfirmed ? 'Agent Confirmed' : 'Mark confirmed'}
                                    </button>
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      ))}
                    </div>

                    <textarea
                      rows={2}
                      value={shipment.note ?? ''}
                      onChange={(event) => onLineItemNoteChange(record.id, shipment.id, event.target.value)}
                      onBlur={() => void onAutoSave(record.id, { auto: true })}
                      placeholder="Optional shipment review note"
                      className="mt-4 w-full rounded border border-neutral-300 px-3 py-2 text-sm text-neutral-900"
                    />

                    {rowIssues.length > 0 && (
                      <div className="mt-3 rounded border border-warning/30 bg-warning/10 p-3 text-sm text-neutral-700">
                        {rowIssues.join(' ')}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
